import { BusinessObjectBase } from './base';
import { tenantSchema } from '@schemas/business-objects/tenant';
import type { ActivityMapping } from './activity-mapping';
import type { ConditionWatcher } from './condition-watcher';
import type { LatestProcessDesign } from './latest-process-design';
import type { MessageListener } from './message-listener';
import type { MyUserTask } from './my-user-task';
import type { PendingStateUpdate } from './pending-state-update';
import type { ProcessDesign } from './process-design';
import type { ProcessDesignActivityCount } from './process-design-activity-count';
import type { ProcessDesignHistory } from './process-design-history';
import type { ProcessInstance } from './process-instance';
import type { ProcessInstanceHistory } from './process-instance-history';
import type { ProcessInstanceSummary } from './process-instance-summary';
import type { ServiceTask } from './service-task';
import type { ServiceTaskHistory } from './service-task-history';
import type { TenantAssignment } from './tenant-assignment';
import type { Timer } from './timer';
import type { Token } from './token';
import type { TokenHistory } from './token-history';
import type { TokenLink } from './token-link';
import type { UserTask } from './user-task';
import type { UserTaskHistory } from './user-task-history';
import type { Id } from '../base-types';
import type { TenantConfig } from '../interface-definitions/index';

export interface TenantData {
  readonly id: Id;
  readonly name: string;
  readonly description?: string;
  readonly tenantAssignments?: TenantAssignment[];
  readonly processDesigns?: ProcessDesign[];
  readonly processDesignHistories?: ProcessDesignHistory[];
  readonly processInstances?: ProcessInstance[];
  readonly processInstanceHistories?: ProcessInstanceHistory[];
  readonly pendingStateUpdates?: PendingStateUpdate[];
  readonly tokens?: Token[];
  readonly tokenHistories?: TokenHistory[];
  readonly tokenLinks?: TokenLink[];
  readonly userTasks?: UserTask[];
  readonly userTaskHistories?: UserTaskHistory[];
  readonly serviceTasks?: ServiceTask[];
  readonly serviceTaskHistories?: ServiceTaskHistory[];
  readonly timers?: Timer[];
  readonly messageListeners?: MessageListener[];
  readonly conditionWatchers?: ConditionWatcher[];
  readonly activityMappings?: ActivityMapping[];
  readonly latestProcessDesigns?: LatestProcessDesign[];
  readonly processDesignActivityCounts?: ProcessDesignActivityCount[];
  readonly myUserTasks?: MyUserTask[];
  readonly processInstanceSummaries?: ProcessInstanceSummary[];
}

export interface Tenant extends Readonly<TenantData> {}
type TenantCreateWithChildrenData = Partial<
  Omit<TenantData, 'tenantAssignments'>
> & { tenantAssignments?: Partial<TenantAssignment>[] };

export class Tenant extends BusinessObjectBase {
  static readonly entityName = 'Tenant' as const;
  static readonly plural = 'tenants';
  static schema = tenantSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<Tenant[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new Tenant(data));
  }

  static async findOne(filter?: any): Promise<Tenant | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new Tenant(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<Tenant> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new Tenant(data);
  }

  static async create(data: Omit<TenantData, 'id'>): Promise<Tenant> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Tenant(result);
  }

  static async createMany(data: Omit<TenantData, 'id'>[]): Promise<Tenant[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Tenant(item));
  }

  static async createWithChildren(
    data: TenantCreateWithChildrenData,
  ): Promise<Tenant> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new Tenant(result);
  }

  static async createManyWithChildren(
    data: TenantCreateWithChildrenData[],
  ): Promise<Tenant[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Tenant(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<TenantData>,
  ): Promise<Tenant> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new Tenant(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  static async currentUser(filter: any): Promise<any[]> {
    const url = `${this.baseUrl}/${this.plural}/current-user`;
    const queryParams = new URLSearchParams();
    if (filter !== undefined) queryParams.set('filter', JSON.stringify(filter));
    const qs = queryParams.toString();
    const fullUrl = qs ? `${url}?${qs}` : url;
    return this.get<any[]>(fullUrl);
  }
}
